import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { getArticleSchema } from '../../utils/seoSchemas';
import { EDITORIAL_AUTHOR, INSIGHTS_MODIFIED, INSIGHTS_PUBLISHED } from './constants';

const HormuzTradeShocks = () => {
  const path = '/insights/hormuz-trade-shocks';
  const title = 'Hormuz Disruptions, Oil Flows & Freight: Trade Risk Explainer | TradeRadar';
  const description =
    'Why the Strait of Hormuz matters for crude and product flows, insurance, routing, and macro spillovers—and how to read chokepoint risk without overstating short-term price effects.';

  const jsonLd = getArticleSchema({
    path,
    seo_title: title,
    seo_description: description,
    datePublished: INSIGHTS_PUBLISHED,
    dateModified: INSIGHTS_MODIFIED,
    authorPerson: EDITORIAL_AUTHOR,
  });

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEOHead
        title={title}
        description={description}
        path={path}
        type="article"
        author={EDITORIAL_AUTHOR.name}
        publishedTime={`${INSIGHTS_PUBLISHED}T12:00:00.000Z`}
        modifiedTime={`${INSIGHTS_MODIFIED}T12:00:00.000Z`}
        keywords="Strait of Hormuz trade risk, oil shipping chokepoint, maritime insurance, supply chain shocks"
        jsonLd={jsonLd}
      />

      <header className="mb-8">
        <p className="text-xs text-slate-500 dark:text-on-surface-variant mb-2">
          Insights · Chokepoints · Published {INSIGHTS_PUBLISHED} · Updated {INSIGHTS_MODIFIED}
        </p>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-3">
          From Hormuz disruption to prices and freight: a supply-chain lens
        </h1>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant">
          By {EDITORIAL_AUTHOR.name} ({EDITORIAL_AUTHOR.jobTitle})
        </p>
      </header>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Why a narrow strait scales globally</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          The Strait of Hormuz is a geographic bottleneck for seaborne oil and refined products leaving the
          Persian Gulf. Even when volumes continue to move, the <em>option value</em> of alternative routes
          is limited: longer voyages absorb tanker capacity, raise freight costs, and stretch inventory
          buffers for importers that lack domestic storage or diversified suppliers.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          Political rhetoric and military incidents therefore affect markets through several channels at
          once: expectations of physical interruption, higher war-risk insurance premia, precautionary buying
          by refiners, and portfolio hedging in futures—not solely through immediate barrel loss. TradeRadar’s
          Hormuz hub separates these mechanisms so readers can reason about second-order effects instead of
          treating every headline as a linear supply shock.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Freight, insurance, and utilisation</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          Tanker markets clear on utilisation. A perceived threat that causes owners to avoid the strait—or
          to demand higher rates for transit—can ripple into global freight indices even if crude production
          is unchanged. Similarly, insurers and flag states may issue guidance that slows sailings without a
          formal closure.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          Our calculators and scenario tools on the{' '}
          <Link to="/strait-of-hormuz" className="text-primary-600 dark:text-primary hover:underline">
            Hormuz hub
          </Link>{' '}
          illustrate illustrative ranges; they are not calibrated forecasts. Always pair them with live
          shipping data and official notices from maritime authorities.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Macro and fiscal spillovers</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          Importing economies with limited strategic petroleum reserves or concentrated supplier relationships
          feel passthrough faster than diversified buyers. Exporting governments may experience budget pressure
          when volumes or prices move against them, which can feed back into domestic policy and, indirectly,
          into trade measures tracked elsewhere on TradeRadar.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          This is why we cross-link chokepoint analysis with country profiles and legislation: supply shocks
          and policy responses often arrive together, and separating correlation from causation requires
          reading both sides of the ledger.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">How to use this article responsibly</h2>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2 list-disc list-inside">
          <li>Treat scenario outputs as educational, not prescriptive.</li>
          <li>Verify vessel and port status against primary AIS and operator statements.</li>
          <li>Distinguish between temporary risk premia and sustained physical shortages.</li>
          <li>Consult compliance teams before acting on sanctions or export-control implications.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Related on TradeRadar</h2>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2">
          <li>
            <Link to="/strait-of-hormuz/risk-dashboard" className="text-primary-600 dark:text-primary hover:underline">
              Hormuz risk dashboard
            </Link>
          </li>
          <li>
            <Link to="/insights/methodology" className="text-primary-600 dark:text-primary hover:underline">
              GTI methodology
            </Link>
          </li>
          <li>
            <Link to="/insights" className="text-primary-600 dark:text-primary hover:underline">
              Insights hub
            </Link>
          </li>
        </ul>
      </section>
    </article>
  );
};

export default HormuzTradeShocks;
